/**
 * ManualChecksPage - liste les controles a verifier manuellement sur le
 * dernier scan, et permet de poser une decision (conforme/skip/fail) avec
 * evidence et lien direct vers la console Microsoft.
 *
 * RBAC : admin + auditor peuvent poser des decisions, viewer en lecture seule.
 * Persistance : per-client (cross-scan).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Card, CardHeader, Title3, Body1, Caption1, Button, Spinner,
  MessageBar, MessageBarBody,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, DialogTrigger,
  Textarea, Field, Badge,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle24Filled, DismissCircle24Filled, ArrowSync24Regular,
  Open16Regular, ArrowExportRtl20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../auth/AuthContext';
import { useLatestScan } from '../hooks/useScans';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { manualDecisionsApi, type ManualDecisionValue, type ManualDecision } from '../api/manualDecisionsApi';
import { controlsById, familyLabels } from '../domain/controls';
import { getManualConsoleUrl } from '../domain/manualConsoleUrls';
import { applyManualDecisions, computeScoreBreakdown } from '../domain/scanEngine';

export function ManualChecksPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { tenant } = useActiveTenant();
  const canEdit = user?.role === 'admin' || user?.role === 'auditor';

  const scan = useLatestScan(tenant.id);
  const [decisions, setDecisions] = useState<Record<string, ManualDecision>>({});
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [editing, setEditing]     = useState<{ controlId: string; current?: ManualDecision } | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await manualDecisionsApi.list();
      setDecisions(res.decisions ?? {});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  // Liste des manuels du scan, joint avec leur decision actuelle
  const manualItems = useMemo(() => {
    if (!scan) return [];
    return scan.results
      .filter((r) => r.status === 'manual')
      .map((r) => ({
        controlId:  r.controlId,
        def:        controlsById[r.controlId],
        decision:   decisions[r.controlId],
      }))
      .filter((it) => it.def != null);
  }, [scan, decisions]);

  // Recompute score avec les decisions appliquees pour montrer l impact
  const scoreImpact = useMemo(() => {
    if (!scan) return null;
    const before = computeScoreBreakdown(scan);
    const after  = computeScoreBreakdown(applyManualDecisions(scan, decisions));
    return { before: before.globalScore, after: after.globalScore, delta: after.globalScore - before.globalScore };
  }, [scan, decisions]);

  async function onSaveDecision(controlId: string, decision: ManualDecisionValue, evidence?: string) {
    setError(null);
    try {
      const res = await manualDecisionsApi.upsert({ controlId, decision, evidence });
      setDecisions((prev) => {
        const next = { ...prev };
        if (decision === 'pending') delete next[controlId];
        else                        next[controlId] = res;
        return next;
      });
      setEditing(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div>
      {!embedded && (
        <PageHeader
          title={t('manual.title')}
          description={t('manual.subtitle')}
        />
      )}

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {loading && (
        <Card style={{ padding: 24, textAlign: 'center' }}>
          <Spinner size="small" />
        </Card>
      )}

      {!loading && !scan && (
        <Card>
          <MessageBar intent="info">
            <MessageBarBody>{t('manual.noScan')}</MessageBarBody>
          </MessageBar>
        </Card>
      )}

      {scan && scoreImpact && (
        <Card style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('manual.scoreBefore')}</Caption1>
              <Body1 style={{ fontSize: 28, fontWeight: 700, display: 'block' }}>
                {scoreImpact.before} / 100
              </Body1>
            </div>
            <ArrowExportRtl20Regular style={{ transform: 'scaleX(-1)', color: tokens.colorNeutralForeground3 }} />
            <div>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('manual.scoreAfter')}</Caption1>
              <Body1 style={{
                fontSize: 28, fontWeight: 700, display: 'block',
                color: scoreImpact.delta > 0 ? tokens.colorPaletteGreenForeground2 : tokens.colorNeutralForeground1,
              }}>
                {scoreImpact.after} / 100
              </Body1>
            </div>
            {scoreImpact.delta !== 0 && (
              <Badge appearance="filled" color={scoreImpact.delta > 0 ? 'success' : 'danger'} size="large">
                {scoreImpact.delta > 0 ? '+' : ''}{scoreImpact.delta} pts
              </Badge>
            )}
          </div>
          <Caption1 style={{ marginTop: 8, display: 'block', color: tokens.colorNeutralForeground3 }}>
            {t('manual.scoreHint')}
          </Caption1>
        </Card>
      )}

      {scan && manualItems.length === 0 && (
        <Card>
          <MessageBar intent="success">
            <MessageBarBody>{t('manual.allDone')}</MessageBarBody>
          </MessageBar>
        </Card>
      )}

      {scan && manualItems.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {manualItems.map(({ controlId, def, decision }) => {
            const consoleUrl = getManualConsoleUrl(controlId);
            const hasDecision = decision && decision.decision !== 'pending';
            const decisionColor =
              decision?.decision === 'conforme' ? tokens.colorPaletteGreenForeground2
              : decision?.decision === 'fail'   ? tokens.colorPaletteRedForeground2
              : decision?.decision === 'skip'   ? tokens.colorNeutralForeground3
              : tokens.colorNeutralForeground2;
            return (
              <Card key={controlId} style={{ padding: 16 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <Body1 style={{ fontWeight: 700 }}>{controlId}</Body1>
                      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                        {familyLabels[def.family]}
                      </Caption1>
                      {hasDecision && (
                        <Badge
                          appearance="filled"
                          size="small"
                          color={
                            decision.decision === 'conforme' ? 'success'
                            : decision.decision === 'fail'    ? 'danger'
                            : 'subtle'
                          }
                        >
                          {t(`manual.decision.${decision.decision}`)}
                        </Badge>
                      )}
                    </div>
                    <Body1 style={{ display: 'block', marginBottom: 6 }}>{def.title}</Body1>
                    <Caption1 style={{ color: tokens.colorNeutralForeground2, display: 'block' }}>
                      {def.description}
                    </Caption1>
                    {decision?.evidence && (
                      <Caption1 style={{
                        marginTop: 8, padding: '6px 10px',
                        background: tokens.colorNeutralBackground2,
                        borderRadius: 4, display: 'block',
                        color: tokens.colorNeutralForeground2,
                      }}>
                        <strong>{t('manual.evidence')}:</strong> {decision.evidence}
                      </Caption1>
                    )}
                    {decision && decision.decidedBy && (
                      <Caption1 style={{ display: 'block', marginTop: 4, color: tokens.colorNeutralForeground3, fontSize: 11 }}>
                        {t('manual.decidedBy', {
                          user: decision.decidedBy,
                          date: decision.decidedAt ? new Date(decision.decidedAt).toLocaleDateString() : '',
                        })}
                      </Caption1>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch', minWidth: 200 }}>
                    {consoleUrl && (
                      <Button
                        as="a"
                        href={consoleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        appearance="subtle"
                        size="small"
                        icon={<Open16Regular />}
                      >
                        {t('manual.openConsole')}
                      </Button>
                    )}
                    {canEdit && (
                      <>
                        <Button
                          appearance={decision?.decision === 'conforme' ? 'primary' : 'outline'}
                          size="small"
                          icon={<CheckmarkCircle24Filled />}
                          onClick={() => onSaveDecision(controlId, 'conforme')}
                        >
                          {t('manual.markConforme')}
                        </Button>
                        <Button
                          appearance={decision?.decision === 'fail' ? 'primary' : 'outline'}
                          size="small"
                          icon={<DismissCircle24Filled />}
                          onClick={() => onSaveDecision(controlId, 'fail')}
                        >
                          {t('manual.markFail')}
                        </Button>
                        <Button
                          appearance={decision?.decision === 'skip' ? 'primary' : 'outline'}
                          size="small"
                          onClick={() => onSaveDecision(controlId, 'skip')}
                        >
                          {t('manual.markSkip')}
                        </Button>
                        <Button
                          appearance="subtle"
                          size="small"
                          onClick={() => setEditing({ controlId, current: decision })}
                          style={{ color: decisionColor }}
                        >
                          {decision?.evidence ? t('manual.editEvidence') : t('manual.addEvidence')}
                        </Button>
                        {hasDecision && (
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={<ArrowSync24Regular />}
                            onClick={() => onSaveDecision(controlId, 'pending')}
                          >
                            {t('manual.revoke')}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal evidence */}
      {editing && (
        <EvidenceDialog
          controlId={editing.controlId}
          initialValue={editing.current?.evidence ?? ''}
          decision={editing.current?.decision ?? 'pending'}
          onClose={() => setEditing(null)}
          onSave={async (evidence, decision) => {
            await onSaveDecision(editing.controlId, decision, evidence);
          }}
        />
      )}
    </div>
  );
}

function EvidenceDialog({
  controlId, initialValue, decision, onClose, onSave,
}: {
  controlId: string;
  initialValue: string;
  decision: ManualDecisionValue;
  onClose: () => void;
  onSave: (evidence: string, decision: ManualDecisionValue) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [value, setValue]       = useState(initialValue);
  const [saving, setSaving]     = useState(false);

  async function submit() {
    setSaving(true);
    try {
      // Si decision est 'pending', on force a 'conforme' par defaut (le user a
      // ouvert la modal pour AJOUTER une evidence, il doit fixer une decision).
      const finalDecision: ManualDecisionValue = decision === 'pending' ? 'conforme' : decision;
      await onSave(value.trim(), finalDecision);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={true} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('manual.evidenceDialog.title', { controlId })}</DialogTitle>
          <DialogContent>
            <Field
              label={t('manual.evidenceDialog.label')}
              hint={t('manual.evidenceDialog.hint')}
            >
              <Textarea
                value={value}
                onChange={(_, d) => setValue(d.value)}
                rows={6}
                placeholder={t('manual.evidenceDialog.placeholder')}
              />
            </Field>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={onClose} disabled={saving}>
                {t('common.cancel')}
              </Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              onClick={submit}
              disabled={saving}
              icon={saving ? <Spinner size="tiny" /> : undefined}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
